import { type ReactNode, Suspense, useEffect } from "react";
import { Await } from "react-router";

import { DividerLine } from "~/components/ui/divider-line";
import { Label } from "~/components/ui/label";
import { cn } from "~/lib/ui";
import { useTileImpressions } from "~/routes/resources/gtm-impressions";
import { ProductPrice } from "~/shop/components/product-price";
import type { PriceDisplay } from "~/shop/utils/price-display";

import type { CrossSellItem } from "../../resources/schemas/basket";
import { useCrossSellProducts } from "../hooks/use-cross-sell-products";
import type { HybridMeasurement } from "../hooks/use-hybrid-detection";
import type { ProductPageData } from "../loader/product-detail-loader";
import {
  type CrossSellProduct,
  CrossSellSection,
  CrossSellSkeleton,
} from "./cross-sell-section";
import { PDPTabs } from "./product-tabs";

// GA4 `view_item_list` identifiers for the PDP "Complete Your Purchase" recs.
// SFRA parity: `dataLayerHelper.js:247-248` (`cyp_pdp` / "Complete Your
// Purchase"). Shared by BOTH cross-sell sources (manual + constructor) so the
// list reports as one stream in GA4 (SFCC-11318). NOTE (constructor path only):
// the CIO pod backing those recs is `pdp-2`, still flagged `TODO: confirm with
// stakeholders` in `use-cross-sell-products.ts` — if that pod id changes, this
// mapping should be re-confirmed. The manual path has no pod.
const CYP_LIST_ID = "cyp_pdp";
const CYP_LIST_NAME = "Complete Your Purchase";

export function PriceLabel({
  display,
  className = "text-lg font-normal",
  salePriceClassName = "text-sale-price font-bold",
  "data-testid": dataTestId,
}: {
  display: PriceDisplay | null;
  className?: string;
  // Sale-price color/weight. `text-sale-price` is the per-site sale color
  // (CSS var `--sale-price-color`, themed under .HSASTORE/.FSASTORE/.WDH).
  salePriceClassName?: string;
  // Per-surface test id from the caller (e.g. `pdp-price`). No default.
  "data-testid"?: string;
}) {
  return (
    <ProductPrice
      display={display}
      as={Label}
      className={className}
      strikeClassName="pr-1 text-gray-500"
      salePriceClassName={cn("pl-1", salePriceClassName)}
      data-testid={dataTestId}
    />
  );
}

export function ProductBadges({
  badges,
  productId,
}: {
  badges: string[];
  productId: string;
}) {
  if (!badges.length) return null;
  return (
    <div className="flex flex-wrap gap-1">
      {badges.map((badge, i) => (
        <span
          key={`${productId}-badge-${i}`}
          className="bg-accent px-1.5 py-0.5 text-xs font-medium text-primary-foreground"
        >
          {badge}
        </span>
      ))}
    </div>
  );
}

/**
 * PDP "Complete your purchase with:" cross-sell entry point (SFCC-11318).
 *
 * The source is resolved per product server-side (`c_crossSellSource`, default
 * `manual`) and arrives on the deferred `crossSell` loader payload:
 *   - `manual` (launch default): SSR-hydrated BM catalog recommendations →
 *     <ManualCrossSell>. SFRA parity with `getOrderableRecommendations`.
 *   - `constructor` (per-product opt-in): CIO client SDK → <CioCrossSell>.
 *
 * Own Suspense boundary so only this section shows the skeleton while the
 * deferred payload streams in — not the whole info column above it.
 */
export function CrossSellProducts({
  product,
  crossSell,
  onTotalsChange,
  onSelectionsChange,
}: {
  product: Record<string, unknown>;
  crossSell: ProductPageData["crossSell"];
  onTotalsChange?: (total: number, items: number) => void;
  onSelectionsChange?: (items: CrossSellItem[]) => void;
}) {
  return (
    <Suspense
      fallback={
        <CrossSellColumn>
          <CrossSellSkeleton />
        </CrossSellColumn>
      }
    >
      <Await resolve={crossSell} errorElement={null}>
        {payload =>
          payload.source === "manual" ? (
            <ManualCrossSell
              products={payload.products}
              onTotalsChange={onTotalsChange}
              onSelectionsChange={onSelectionsChange}
            />
          ) : (
            <CioCrossSell
              product={product}
              onTotalsChange={onTotalsChange}
              onSelectionsChange={onSelectionsChange}
            />
          )
        }
      </Await>
    </Suspense>
  );
}

/**
 * Info-column wrapper shared by the skeleton fallback and rendered section so
 * the skeleton→content swap doesn't shift horizontally.
 */
function CrossSellColumn({ children }: { children: ReactNode }) {
  return <div className="w-full px-5 sm:px-0">{children}</div>;
}

/**
 * Leading + trailing rules bracketing the cross-sell section, plus the padded
 * column. Rendered INSIDE the `Await` boundary and — crucially — NOT gated on
 * the effect-derived `crossSellItems` parent state. That state is only set from
 * a `useEffect` in <CrossSellSection>, which never runs during SSR, so the
 * dividers used to appear only after hydration and visibly popped in on first
 * paint even though the manual section was already server-rendered. Bracketing
 * the section here ties the dividers to the resolved payload, so they ship in
 * the SSR HTML whenever the section does (SFCC-11318 follow-up).
 */
function CrossSellBlock({ children }: { children: ReactNode }) {
  return (
    <>
      <DividerLine className="mt-4 px-5 sm:px-0" />
      <CrossSellColumn>{children}</CrossSellColumn>
      <DividerLine className="my-4 px-5 sm:px-0" />
    </>
  );
}

/**
 * Manual cross-sell — BM-assigned catalog recommendations, SSR-hydrated by the
 * loader (SFCC-11318). No CIO pod/result/strategy ⇒ no `recommendation_click`
 * beacon (CrossSellSection skips it when `podId` is undefined). Renders nothing
 * when there are no orderable recs (SFRA parity: section omitted entirely).
 */
function ManualCrossSell({
  products,
  onTotalsChange,
  onSelectionsChange,
}: {
  products: CrossSellProduct[];
  onTotalsChange?: (total: number, items: number) => void;
  onSelectionsChange?: (items: CrossSellItem[]) => void;
}) {
  // GA4 `view_item_list` via the SFCC-11013 server endpoint ("list mode") so
  // item_category/brand/price are real server-resolved SCAPI rather than the
  // blank-category / CIO-stringified-brand the old client path emitted.
  // `categoryByPid` reuses THIS batch's server-resolved category paths for the
  // tile-click `select_item` below — no second lookup (SFCC-12175 W3).
  const { categoryByPid } = useTileImpressions(
    products.map(p => p.id).filter(Boolean),
    CYP_LIST_ID,
    { listName: CYP_LIST_NAME },
  );
  if (!products.length) return null;
  return (
    <CrossSellBlock>
      <CrossSellSection
        products={products}
        categoryByPid={categoryByPid}
        onTotalsChange={onTotalsChange}
        onSelectionsChange={onSelectionsChange}
      />
    </CrossSellBlock>
  );
}

/**
 * Constructor cross-sell — the per-product opt-in (CIO client SDK, SFCC-10692).
 * First paint shows a skeleton; after hydration the SDK call resolves and the
 * `recommendation_click` beacon is wired via pod/result/strategy ids.
 */
function CioCrossSell({
  product,
  onTotalsChange,
  onSelectionsChange,
}: {
  product: Record<string, unknown>;
  onTotalsChange?: (total: number, items: number) => void;
  onSelectionsChange?: (items: CrossSellItem[]) => void;
}) {
  const {
    products,
    isLoading,
    hasRecommendations,
    podId,
    resultId,
    strategyIds,
  } = useCrossSellProducts(product);

  // GA4 `view_item_list` via the SFCC-11013 server endpoint (see ManualCrossSell).
  // The CIO `recommendation_click` beacon stays on the CIO client (unaffected).
  // See ManualCrossSell — same reuse of the resolved category paths.
  const { categoryByPid } = useTileImpressions(
    products.map(p => p.id).filter(Boolean),
    CYP_LIST_ID,
    { listName: CYP_LIST_NAME },
  );

  if (isLoading)
    return (
      <CrossSellColumn>
        <CrossSellSkeleton />
      </CrossSellColumn>
    );
  if (!hasRecommendations) return null;
  return (
    <CrossSellBlock>
      <CrossSellSection
        products={products}
        categoryByPid={categoryByPid}
        isLoading={isLoading}
        podId={podId}
        resultId={resultId}
        strategyIds={strategyIds}
        onTotalsChange={onTotalsChange}
        onSelectionsChange={onSelectionsChange}
      />
    </CrossSellBlock>
  );
}

/**
 * Reports whether the RESOLVED product takes the hybrid path, from inside the
 * product `<Await>` — the only place that can know it on a client navigation.
 *
 * `productId` is what the reading is ABOUT, and it has to arrive as a prop
 * rather than be read from the route at effect time. This component lives
 * inside the Suspense boundary, so on a PDP→PDP soft nav its COMMITTED props
 * still describe the previous product while the route has already moved on to
 * the next one — which is exactly the window in which a reading must not be
 * mistaken for the new product's. Sending the id along with `isHybrid` keeps
 * the pair honest; `useHybridDetection` then discards it on mismatch.
 */
export function HybridDetector({
  productId,
  isHybrid,
  onHybridChange,
}: {
  productId: string;
  isHybrid: boolean;
  onHybridChange: (measurement: HybridMeasurement) => void;
}) {
  useEffect(() => {
    onHybridChange({ productId, isHybrid });
  }, [productId, isHybrid, onHybridChange]);
  return null;
}

export const PDPTabsRender = ({
  content,
}: {
  content: Awaited<ProductPageData["originalTabsContent"]>;
}) => {
  const items = content?.data?.items;
  if (!items || !items.length) return null;
  return <PDPTabs content={items} />;
};
